import type { Dispatch, SetStateAction } from "react";
import type { MainTab, ReportSubTab } from "../types";
import type { ReportValidationIssue } from "../validation/issues";

type ValidationNavigationActions = {
    setValidationDialogOpen: (open: boolean) => void;
    setPendingReportNavigation: (navigation: null) => void;
    setMainTab: (tab: MainTab) => void;
    setReportSubTab: (tab: ReportSubTab) => void;
    setOpenRuleSetId: (id: string) => void;
    setOpenRuleIdsBySet: Dispatch<SetStateAction<Record<string, string>>>;
    setOpenMetricId: (id: string) => void;
    setOpenExcelMetricId: (id: string) => void;
};

export const focusValidationTarget = (fieldKey?: string) => {
        if (!fieldKey) return;
        const focusAttempt = (attempt: number) => {
            const target = Array.from(document.querySelectorAll<HTMLElement>("[data-rc-validation]"))
                .find((item) => item.getAttribute("data-rc-validation") === fieldKey)
                || document.getElementById(`${fieldKey}-trigger`);

            if (!target) {
                if (attempt < 10) window.setTimeout(() => focusAttempt(attempt + 1), 80);
                return;
            }

            target.scrollIntoView({ behavior: "smooth", block: "center" });
            target.classList.remove("rc-validation-focus-pulse");
            window.requestAnimationFrame(() => target.classList.add("rc-validation-focus-pulse"));
            window.setTimeout(() => target.classList.remove("rc-validation-focus-pulse"), 1600);
            const focusable = target.matches("input, button, [tabindex]")
                ? target
                : target.querySelector("input, button, [tabindex]") as HTMLElement | null;
            focusable?.focus?.({ preventScroll: true });
        };

        window.requestAnimationFrame(() => focusAttempt(0));
    };

export const openValidationIssue = (issue: ReportValidationIssue, { setValidationDialogOpen, setPendingReportNavigation, setMainTab, setReportSubTab, setOpenRuleSetId, setOpenRuleIdsBySet, setOpenMetricId, setOpenExcelMetricId }: ValidationNavigationActions) => {
        setValidationDialogOpen(false);
        setPendingReportNavigation(null);
        setMainTab("reports");
        setReportSubTab(issue.tab);
        if (issue.tab === "categorization" && issue.ruleSetId) {
            setOpenRuleSetId(issue.ruleSetId);
            if (issue.ruleId) setOpenRuleIdsBySet((current) => ({ ...current, [issue.ruleSetId!]: issue.ruleId! }));
        }
        if (issue.tab === "metrics" && issue.sectionId) setOpenMetricId(issue.sectionId);
        if (issue.tab === "excel" && issue.sectionId) setOpenExcelMetricId(issue.sectionId);
        focusValidationTarget(issue.fieldKey);
    };
